import { Box, Flex, Icon, SimpleGrid, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import { useMemo } from 'react';
import { IconType } from 'react-icons';
import { MdPeople, MdSportsHandball, MdSwapVert, MdTimer } from 'react-icons/md';
import Card from 'components/card/Card';
import MiniStatistics from 'components/card/MiniStatistics';
import { AnalysisResult, PLAYER_COLORS, formatDuration } from '../types';
import { translate } from '../../../i18n';

interface Props {
  data: AnalysisResult;
}

function IconBox({ icon, color }: { icon: IconType; color: string }) {
  return (
    <Flex align="center" justify="center" borderRadius="50%" w="40px" h="40px" bg={color}>
      <Icon as={icon} w="22px" h="22px" color="white" />
    </Flex>
  );
}

export default function PlayerStatsCards({ data }: Props) {
  const playerIds = useMemo(() => Object.keys(data.stats), [data.stats]);
  const totalShots = useMemo(() => data.events.filter((e) => e.type === 'shot').length, [data.events]);
  const totalRallies = useMemo(() => data.events.filter((e) => e.type === 'rally_end').length, [data.events]);
  const duration = data.video.duration_seconds;
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  return (
    <Box>
      <SimpleGrid columns={{ base: 2, md: 4 }} gap="20px" mb={6}>
        <MiniStatistics
          startContent={<IconBox icon={MdPeople} color="#38A169" />}
          name={translate('court_recording.players')}
          value={playerIds.length}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdSportsHandball} color="#E53E3E" />}
          name={translate('court_recording.total_shots')}
          value={totalShots}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdSwapVert} color="#3182CE" />}
          name={translate('court_recording.rallies')}
          value={totalRallies}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdTimer} color="#805AD5" />}
          name={translate('court_recording.duration')}
          value={formatDuration(duration)}
        />
      </SimpleGrid>

      <Card px="20px" py="15px">
        <Text fontSize="md" fontWeight="600" color={textColor} mb={3}>{translate('court_recording.player_details')}</Text>
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{translate('court_recording.player')}</Th>
                <Th isNumeric>{translate('court_recording.coverage')}</Th>
                <Th isNumeric>{translate('court_recording.shots')}</Th>
                <Th isNumeric>{translate('court_recording.distance')}</Th>
                <Th isNumeric>{translate('court_recording.avg_speed')}</Th>
                <Th isNumeric>{translate('court_recording.time_visible')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {playerIds.map((pid, i) => {
                const s = data.stats[pid];
                return (
                  <Tr key={pid}>
                    <Td>
                      <Flex align="center" gap={2}>
                        <Box w="10px" h="10px" borderRadius="50%" bg={PLAYER_COLORS[i % PLAYER_COLORS.length]} />
                        <Text color={textColor} fontWeight="500">{translate('court_recording.player')} {pid}</Text>
                      </Flex>
                    </Td>
                    <Td isNumeric>{s.court_coverage_pct}%</Td>
                    <Td isNumeric>{s.shots}</Td>
                    <Td isNumeric>
                      {s.total_distance_m != null
                        ? `${s.total_distance_m.toLocaleString()}m`
                        : `${s.total_distance_px.toLocaleString()}px`}
                    </Td>
                    <Td isNumeric>
                      {s.avg_speed_m_per_sec != null
                        ? `${s.avg_speed_m_per_sec.toFixed(2)} m/s`
                        : `${s.avg_speed_px_per_sec.toFixed(1)} px/s`}
                    </Td>
                    <Td isNumeric>{s.time_visible_sec.toFixed(1)}s</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
}
